// R2-01L A4-02 · shared read-only quantity display rules (backend stays authoritative).
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

export const LEGACY_SPLIT_LABEL = "Legacy Cancelled/Closed — split unavailable";

export const PROTECTION_TEXT = {
  REVIEW: "Under review — quantity changes are blocked for this line",
  LEGACY_EXCEPTION: "Legacy exception — quantity changes are blocked for this line",
  EVIDENCE_PENDING: "Original quantity evidence pending — quantity changes are blocked",
};

// D1 · stored legacy total with no established original → never split into Cancel / Close.
export const legacySplitUnavailable = (pos) =>
  !!pos && (pos.O === null || pos.O === undefined) && Number(pos.cancelled_qty || 0) > 0;

const statusKey = (s) => String(s || "").trim().toLowerCase().replace(/[-_]/g, " ").replace(/\s+/g, " ");
// Mirrors backend `pending_report.order_eligible` (PSOR-01 G1 + Phase 2C-5B).
const INELIGIBLE = new Set(["hold", "on hold", "short closed", "shortclosed", "short close", "cancelled", "canceled",
  "completed", "closed", "rejected", "draft", "pending approval"]);

export const isOrderCancelled = (o) => ["cancelled", "canceled"].includes(statusKey(o?.status));
const isAuthorised = (otype, o) => (otype === "eso" ? o?.auth_status === "Authorised" : o?.authorized === true);
export const isActionablePending = (otype, o) => isAuthorised(otype, o) && !INELIGIBLE.has(statusKey(o?.status));
// D4 · submitted, not yet authorised (Draft / Rejected excluded from both totals).
export const isSubmittedPendingApproval = (otype, o) => !isAuthorised(otype, o) && !isOrderCancelled(o)
  && (statusKey(o?.approval_status) === "pending approval" || (otype === "eso" && o?.auth_status === "PendingApproval"));

// D2 · Administrator or the existing order-view grant (no new permission).
export const QTY_VIEW_PERM = { so: "sales_orders:view", eso: "exports:view" };
export const canViewQty = (user, hasPerm, otype) => user?.role === "Administrator" || !!hasPerm?.(QTY_VIEW_PERM[otype]);

export function useQtyPositions(otype, orderId, enabled) {
  const [byRef, setByRef] = useState({});
  const load = useCallback(async () => {
    if (!enabled || !orderId) { setByRef({}); return; }
    try {
      const { data } = await api.get(`/orders/${otype}/${orderId}/qty-positions`);
      setByRef(Object.fromEntries((data.lines || []).map((l) => [l.line_ref, l])));
    } catch { setByRef({}); }
  }, [otype, orderId, enabled]);
  useEffect(() => { load(); }, [load]);
  return [byRef, load];
}
